import { useMemo } from 'react';

import { useAuth } from '../context/AuthContext';
import { useTimesheetDepartmentListQuery } from './react-query/useTimesheetDepartmentQuery';

/**
 *
 * @param options Configuration options for the hook
 * @param options.includeAllForPayroll If true, users with payroll permission will see all departments
 * @returns null if the query is not success, otherwise an array of departments
 */
export const useLoggedInUserManagedDepartments = (
  options: { includeAllForPayroll?: boolean } = {},
) => {
  const { includeAllForPayroll = false } = options;
  const auth = useAuth();
  const departmentsQuery = useTimesheetDepartmentListQuery({
    filter: {
      isActive: 1,
    },
  });

  const allowedDepartments = useMemo(() => {
    if (!departmentsQuery.isSuccess) return null;

    // Check if user has payroll permission
    const hasPayrollPermission = auth.user.permissionGroups.some(
      (p) => p.type === 'role' && p.name === 'Payroll',
    );

    // If user has payroll permission and we want to include all departments, return all
    if (hasPayrollPermission && includeAllForPayroll) {
      return departmentsQuery.data?.results;
    }

    try {
      const departmentIds = auth.user.permissionGroups
        .filter((p) => p.type == 'department')
        .reduce<string[]>((aggr, item) => [...aggr, JSON.parse(item.filterData).value], [])
        .map(Number);

      if (departmentIds.length > 0) {
        return departmentsQuery.data?.results.filter((p) => departmentIds.includes(p.id));
      } else {
        return departmentsQuery.data?.results.filter(
          (p) => p.id.toString() == auth.user.employeeDepartmentID.toString(),
        );
      }
    } catch {
      console.error('Error getting allowed departments');
      return [];
    }
  }, [
    auth.user.employeeDepartmentID,
    auth.user.permissionGroups,
    departmentsQuery.data?.results,
    departmentsQuery.isSuccess,
    includeAllForPayroll,
  ]);

  return allowedDepartments;
};
